import React, { useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useT } from '../../i18n';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

/**
 * Host's remove-player control — one raised plate, two presses.
 *
 * The first press arms it (the plate turns tomato and reads KICK?), the second
 * removes the player. It disarms itself after 3s, so a mis-press on a crowded
 * scoreboard costs nothing. Shared by the desktop ScoreBoard and the mobile
 * PlayersSheet so both halves of the app can't drift apart.
 */
const useStyles = createUseStyles({
  btn: {
    height: ({ size }) => `${size}px`,
    minWidth: ({ size }) => `${size}px`,
    padding: 0,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#3a2416',
    border: 'none',
    borderRadius: '6px',
    color: '#c08a6a',
    cursor: 'pointer',
    boxShadow: [
      'inset 0 1px 0 rgba(255,255,255,0.12)',
      'inset 0 -2px 0 rgba(0,0,0,0.34)',
      '0 2px 0 #1c1108',
    ].join(','),
    transition: 'background .12s ease, transform .06s ease, box-shadow .06s ease',
    '&:hover': { background: '#4d2a18' },
    '&:active': {
      transform: 'translateY(2px)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), inset 0 2px 4px rgba(0,0,0,0.45)',
    },
  },
  btnArmed: {
    padding: '0 8px',
    background: '#7d2a12',
    boxShadow: [
      'inset 0 1px 0 rgba(255,255,255,0.16)',
      'inset 0 -2px 0 rgba(0,0,0,0.34)',
      '0 2px 0 #431407',
    ].join(','),
    '&:hover': { background: '#93331a' },
  },
  // Two crossed bars — an ✕ without an icon font.
  mark: {
    position: 'relative',
    width: '10px',
    height: '10px',
    '&::before, &::after': {
      content: '""',
      position: 'absolute',
      top: '4px',
      left: 0,
      width: '10px',
      height: '2px',
      borderRadius: '1px',
      background: '#c08a6a',
    },
    '&::before': { transform: 'rotate(45deg)' },
    '&::after': { transform: 'rotate(-45deg)' },
  },
  label: {
    fontFamily: MONO,
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '0.12em',
    color: '#f6dccd',
    whiteSpace: 'nowrap',
  },
});

function KickButton({ username, onKick, size = 28 }) {
  const classes = useStyles({ size });
  const t = useT();
  const [armed, setArmed] = useState(false);

  // The effect owns the disarm timer, so unmounting the row (or the whole
  // panel) cancels it instead of leaving an armed kick behind.
  useEffect(() => {
    if (!armed) return undefined;
    const timer = setTimeout(() => setArmed(false), 3000);
    return () => clearTimeout(timer);
  }, [armed]);

  const press = (event) => {
    event.stopPropagation();
    if (!armed) {
      setArmed(true);
      return;
    }
    setArmed(false);
    onKick();
  };

  const name = username ?? 'player';

  return (
    <button
      type="button"
      onClick={press}
      title={armed ? `Confirm removing ${name}` : `Remove ${name}`}
      aria-label={armed ? `Confirm removing ${name}` : `Remove ${name}`}
      className={`${classes.btn} ${armed ? classes.btnArmed : ''}`}
    >
      {armed ? <span className={classes.label}>{t('game.kickConfirm')}</span> : <span className={classes.mark} />}
    </button>
  );
}

export default KickButton;
